import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useHistory} from "react-router-dom";
import {Formik} from 'formik';
import moment from "moment";
import {useDispatch} from 'react-redux';
import ButtonAction from '../../../_common/components/ButtonAction';
import FilterText from '../../../_common/components/FilterText';
import FilterSelect from '../../../_common/components/FilterSelect';
import {FilterRangePicker} from '../../../_common/components/FieldDatepicker';
import ClearIcon from '../../../_common/components/ClearIcon';
import {usePriceQuotesPageContext} from '../PriceQuotesPageContext';
import {formatStringToFloatNumber, integerValue} from '../../../_common/utils/changeInputValue';
import {applyFilter} from '../../../../../utils/prepareApplyFilter';
import {priceQuoteStatus, priceQuoteCustomerStatus} from '../../../_common/constants/options';
import {generateExcelFile} from '../../../_common/utils/excel';
import {columnNamesPriceQuotes} from '../../../_common/constants/excelColumnNames';
import {toAbsoluteUrl} from '../../../../../_metronic/_helpers';
import {getPriceQuotesListForExcel} from '../../_redux/actions/PriceQuotesPageTableActions';
import styles from '../../../_common/styles/filtersBar.module.scss';

export function PriceQuotesPageFilters() {
  const {formatMessage} = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const priceQuotesPageContext = usePriceQuotesPageContext();
  const priceQuotesPageProps = useMemo(() => {
    return {
      queryParams: priceQuotesPageContext.queryParams,
      setQueryParams: priceQuotesPageContext.setQueryParams,
    };
  }, [priceQuotesPageContext]);
  const [loading, setLoading] = useState(false);

  const prefix = 'filter-placeholder';

  const qs = new URLSearchParams(window.location.search);
  let priceQuoteID = qs.get('priceQuoteID') || '';
  let fromCreateDate = qs.get('fromCreateDate') || '';
  let toCreateDate = qs.get('toCreateDate') || '';
  let status = qs.get('status') || '';
  let fromPrice = qs.get('fromPrice') || '';
  let toPrice = qs.get('toPrice') || '';
  let customerStatus = qs.get('customerStatus') || '';

  const handleExport = () => {
    setLoading(true);
    dispatch(getPriceQuotesListForExcel(priceQuotesPageProps.queryParams))
      .then(prepareDataForExcel)
      .then((data) => {
        generateExcelFile(
          data,
          'Price_quotes.xlsx',
          'הצעות מחיר'
        );
      })
      .catch((error) => {
        console.error(`Error: + ${error}`);
      }).finally(() => setLoading(false));
  };
  const prepareDataForExcel = data => {
    return data.map((item, index) => {
      const {
        priceQuoteID,
        createDate,
        customerFirstNames,
        customerLastNames,
        contactFirstName,
        contactLastName,
        priceQuoteStatusName,
        price,
        sentTo,
        sentDate,
        priceQuoteCustomerStatusName
      } = item;

      return {
        [columnNamesPriceQuotes[0]]: index + 1,
        [columnNamesPriceQuotes[1]]: priceQuoteID || '',
        [columnNamesPriceQuotes[2]]: createDate ? moment(createDate).format('DD-MM-YYYY') : '',
        [columnNamesPriceQuotes[3]]: [customerFirstNames, customerLastNames].filter(Boolean).join(' ') || '',
        [columnNamesPriceQuotes[4]]: [contactFirstName, contactLastName].filter(Boolean).join(' ') || '',
        [columnNamesPriceQuotes[5]]: priceQuoteStatusName || '',
        [columnNamesPriceQuotes[6]]: price || '0',
        [columnNamesPriceQuotes[7]]: sentTo || '',
        [columnNamesPriceQuotes[8]]: sentDate ? moment(sentDate).format('DD-MM-YYYY') : '',
        [columnNamesPriceQuotes[9]]: priceQuoteCustomerStatusName || '',
      };
    });
  };

  return (
    <Formik
      initialValues={{
        priceQuoteID,
        fromCreateDate,
        toCreateDate,
        status,
        fromPrice,
        toPrice,
        customerStatus
      }}
      onSubmit={(values) => applyFilter(values, priceQuotesPageProps)}
      enableReinitialize={true}
    >
      {({values, handleSubmit, handleBlur, setFieldValue}) => (
        <form onSubmit={handleSubmit} className={styles.container}>
          <div className={styles.filtersContainer}>
            <FilterText
              fieldName={'priceQuoteID'}
              value={values.priceQuoteID}
              placeholder={formatMessage({id: `${prefix}-price-quote-id`, defaultMessage: 'Price quote number'})}
              formatValue={integerValue}
              containerClassnames={styles.filter}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
              handleBlur={handleBlur}
            />
            <div className={styles.filter}>
              <FilterRangePicker
                onChange={(fromDate, toDate) => {
                  setFieldValue('fromCreateDate', fromDate || '');
                  setFieldValue('toCreateDate', toDate || '');
                  handleSubmit();
                }}
                placeholder={[formatMessage({id: `${prefix}-create-date-from-to`, defaultMessage: 'Create date'})]}
                startDate={fromCreateDate}
                endDate={toCreateDate}
              />
              <ClearIcon
                onClick={() => {
                  setFieldValue('fromCreateDate', '');
                  setFieldValue('toCreateDate', '');
                  handleSubmit();
                }}
              />
            </div>
            <FilterSelect
              fieldName={'status'}
              valueName={values.status}
              options={priceQuoteStatus}
              placeholder={formatMessage({id: `${prefix}-status`, defaultMessage: 'Status'})}
              containerClassnames={styles.filter}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <FilterText
              fieldName={'fromPrice'}
              value={values.fromPrice}
              placeholder={formatMessage({id: `${prefix}-price-quote-from`, defaultMessage: 'Price from'})}
              formatValue={formatStringToFloatNumber}
              containerClassnames={styles.filter}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
              handleBlur={handleBlur}
            />
            <FilterText
              fieldName={'toPrice'}
              value={values.toPrice}
              placeholder={formatMessage({id: `${prefix}-price-quote-to`, defaultMessage: 'Price to'})}
              formatValue={formatStringToFloatNumber}
              containerClassnames={styles.filter}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
              handleBlur={handleBlur}
            />
            <FilterSelect
              fieldName={'customerStatus'}
              valueName={values.customerStatus}
              options={priceQuoteCustomerStatus}
              placeholder={formatMessage({id: `${prefix}-customerStatus`, defaultMessage: 'Customer status'})}
              containerClassnames={styles.filter}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
          </div>
          <div className={styles.buttonsContainer}>
            <ButtonAction
              useIcon={'/media/common/Plus_white.svg'}
              label={formatMessage({id: 'price-quotes-page-button-create-new', defaultMessage: 'Create price quote'})}
              onClick={() => history.push('/price-quote-details/-1')}
            />
            <ButtonAction
              variant={'success'}
              label={
                <>
                  {loading
                    ? (
                      <div className="spinner-border spinner-border-md m-auto text-light" role="status"/>
                    )
                    : (
                      <>
                        <img
                          src={toAbsoluteUrl('/media/common/Excel_white.svg')}
                          alt="export" className={'mr-2'}
                        />
                        <span>{formatMessage({id: 'button-export-to-excel', defaultMessage: 'Export'})}</span>
                      </>
                    )
                  }
                </>
              }
              onClick={handleExport}
              customClassnames={'w-100px ml-4'}
            />
          </div>
        </form>
      )}
    </Formik>
  );
}

export default PriceQuotesPageFilters;